import React, { useState } from "react";
import { Season } from "../types";
import { LeagueStandings, LeagueRow, rulesLines } from "../utils/league";
import Avatar from "./Avatar";

// ── Liigan taulukko ja sääntökortti ───────────────────────────
//
// Sama komponentti pelaajan Liiga-välilehdellä, liigaillan tapahtuma-
// sivulla ja ylläpitäjän kausiasetuksissa. Sääntöteksti muodostetaan
// kauden luvuista (utils/league.ts), joten se ei voi erota laskennasta.

const C = {
  card: "var(--sl-card)",
  navy: "var(--sl-primary)", gold: "var(--sl-accent)", text: "var(--sl-text)", muted: "var(--sl-muted)", white: "#ffffff",
  border: "var(--sl-border)", soft: "var(--sl-soft)", accent: "var(--sl-success)",
};
const F = { heading: "'Rajdhani', sans-serif", body: "'Sora', sans-serif" };
const card: React.CSSProperties = {
  background: C.card, border: `1px solid ${C.border}`,
  borderRadius: 16, padding: 18, marginBottom: 14,
  boxShadow: "0 4px 16px rgba(22,59,104,0.06)",
};

// Taulukon sarakkeet: #, nimi, pisteet, pisteet/ilta, illat.
//
// Voitto-% oli tassa 2.11.2026 asti. Se vaihdettiin pisteisiin per ilta,
// koska kausi ratkeaa pisteilla eika voitoilla: V-% oli naista kahdesta
// se, joka ei kerro sijoituksesta mitaan. Voitot, haviot ja voitto-%
// ovat yha rivin alla avautuvassa erittelyssa.
const COLS = "22px 1fr 42px 44px 34px";

/** Yhden desimaalin luku illan kielella: 48,8 suomeksi, 48.8 englanniksi. */
const fmtAvg = (n: number, lang: "fi" | "en") =>
  n.toLocaleString(lang === "fi" ? "fi-FI" : "en-US", {
    minimumFractionDigits: 1, maximumFractionDigits: 1,
  });

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("fi-FI", { day: "numeric", month: "numeric" });

interface CommonProps {
  season: Season;
  standings: LeagueStandings | null;
  loading: boolean;
  /** Oma rivi korostetaan: playerId tai nimi. */
  myPlayerId?: string;
  myName?: string;
  /** Profiilikuvat playerId:n mukaan; korokkeen ympyra kayttaa naita. */
  photos?: Record<string, string>;
  lang?: "fi" | "en";
}

function findMine(standings: LeagueStandings | null, myPlayerId?: string, myName?: string): { row: LeagueRow; rank: number } | null {
  if (!standings) return null;
  const i = standings.rows.findIndex(r =>
    (myPlayerId && r.playerId === myPlayerId) ||
    (myName && r.name.toLowerCase() === myName.toLowerCase())
  );
  return i >= 0 ? { row: standings.rows[i], rank: i + 1 } : null;
}

/** Säännöt avattavana rivinä, ja oma tilanne jos pelaaja on taulukossa. */
export const LeagueRulesCard: React.FC<CommonProps & { defaultOpen?: boolean }> = ({
  season, standings, myPlayerId, myName, lang = "fi", defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const mine = findMine(standings, myPlayerId, myName);
  const lines = rulesLines(season.rules, lang);

  return (
    <div style={{ ...card, padding: "12px 16px" }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", background: "none", border: "none", cursor: "pointer", padding: 0,
        display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: F.body,
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>
          🏆 {season.name} · {lang === "fi" ? "Näin pisteet lasketaan" : "How points work"}
        </span>
        <span style={{ fontSize: 12, color: C.muted }}>{open ? "▲" : "▼"}</span>
      </button>

      {mine && (
        <div style={{ fontSize: 12, color: C.text, marginTop: 8 }}>
          {lang === "fi"
            ? <>Olet tällä hetkellä <strong>{mine.rank}.</strong> · {mine.row.points} p
              {mine.row.pointsPerNight !== null ? ` · ${fmtAvg(mine.row.pointsPerNight, lang)} p/ilta` : ""} · {mine.row.wins}–{mine.row.losses}
              {mine.row.winPct !== null ? ` (${fmtAvg(mine.row.winPct, lang)} %)` : ""} · {mine.row.played} {mine.row.played === 1 ? "ilta" : "iltaa"} pelattu
              {mine.row.played > season.rules.countBest ? ` · ${season.rules.countBest} parasta lasketaan` : ""}
              {!mine.row.eligible ? ` · palkintoon vielä ${season.rules.minEvents - mine.row.played} ${season.rules.minEvents - mine.row.played === 1 ? "ilta" : "iltaa"}` : ""}</>
            : <>You are currently <strong>{mine.rank}.</strong> · {mine.row.points} pts
              {mine.row.pointsPerNight !== null ? ` · ${fmtAvg(mine.row.pointsPerNight, lang)} pts/n` : ""} · {mine.row.wins}–{mine.row.losses}
              {mine.row.winPct !== null ? ` (${fmtAvg(mine.row.winPct, lang)}%)` : ""} · {mine.row.played} night{mine.row.played === 1 ? "" : "s"} played
              {mine.row.played > season.rules.countBest ? ` · best ${season.rules.countBest} count` : ""}
              {!mine.row.eligible ? ` · ${season.rules.minEvents - mine.row.played} more for prizes` : ""}</>}
        </div>
      )}

      {open && (
        <ul style={{ margin: "10px 0 0", paddingLeft: 18, fontSize: 12, color: C.text, lineHeight: 1.7 }}>
          {lines.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      )}
    </div>
  );
};

/** Kauden taulukko. Oma rivi korostettuna, rivin saa auki iltakohtaisiin pisteisiin. */
export const LeagueTable: React.FC<CommonProps> = ({ season, standings, loading, myPlayerId, myName, photos, lang = "fi" }) => {
  const [openKey, setOpenKey] = useState<string | null>(null);

  if (loading) {
    return <div style={{ textAlign: "center", padding: 24, color: C.muted, fontSize: 13 }}>{lang === "fi" ? "Ladataan…" : "Loading…"}</div>;
  }
  if (!standings || standings.rows.length === 0) {
    return (
      <div style={{ ...card, textAlign: "center", color: C.muted, fontSize: 13, lineHeight: 1.6 }}>
        {lang === "fi"
          ? <>Ei vielä pisteitä. Taulukko täyttyy, kun ensimmäinen liigailta on päätetty.<br />
              {standings ? `${standings.totalNights} iltaa kaudella.` : ""}</>
          : <>No points yet. The table fills in once the first league night has ended.<br />
              {standings ? `${standings.totalNights} nights in the season.` : ""}</>}
      </div>
    );
  }

  const isMine = (r: LeagueRow) =>
    (!!myPlayerId && r.playerId === myPlayerId) ||
    (!!myName && r.name.toLowerCase() === myName.toLowerCase());

  const top = standings.rows.slice(0, 3);
  // Kauden eteneminen lohkoina: pelatut illat keltaisena.
  const progress = standings.totalNights > 0 && (
    <div style={{ ...card, padding: "9px 14px", display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700, color: C.muted, whiteSpace: "nowrap" }}>{lang === "fi" ? "Kausi" : "Season"}</span>
      <span style={{ flex: 1, display: "grid", gridTemplateColumns: `repeat(${standings.totalNights}, minmax(0, 1fr))`, gap: 3 }}>
        {Array.from({ length: standings.totalNights }, (_, i) => (
          <span key={i} style={{ height: 8, borderRadius: 3, background: i < standings.playedNights ? C.gold : C.soft }} />
        ))}
      </span>
      <span style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 14, color: C.navy, whiteSpace: "nowrap" }}>{standings.playedNights} / {standings.totalNights}</span>
    </div>
  );
  // Koroke kärkikolmikolle: 2. – 1. – 3., voittaja korkeimpana.
  const podiumCard = (r: LeagueRow, rank: number) => (
    <div key={r.key} style={{
      display: "flex", flexDirection: "column", alignItems: "center", gap: 4, textAlign: "center",
      background: C.card, borderRadius: 14, padding: rank === 1 ? "14px 6px 10px" : "10px 6px 8px",
      border: rank === 1 ? `2px solid ${C.gold}` : `1px solid ${C.border}`,
      boxShadow: rank === 1 ? "0 10px 24px var(--sl-accent-a20)" : undefined,
    }}>
      {/* Sama Avatar kuin muualla: profiilikuva jos sellainen on, muuten
          nimen ensimmainen kirjain. Tassa oli oma ympyra, joten liigan
          koroke oli ainoa paikka jossa kuva ei nakynyt. */}
      <Avatar
        name={r.name}
        photoUrl={r.playerId ? photos?.[r.playerId] : undefined}
        size={rank === 1 ? 40 : 34}
        background={isMine(r) ? C.gold : C.navy}
        color={isMine(r) ? C.text : C.white}
        fontSize={rank === 1 ? 15 : 13}
      />
      <div style={{ fontSize: 12, fontWeight: 700, color: C.text, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</div>
      <div style={{ fontFamily: F.heading, fontWeight: 700, fontSize: rank === 1 ? 26 : 20, lineHeight: 1, color: C.navy }}>{r.points}</div>
      {rank === 1
        ? <span style={{ fontSize: 10, fontWeight: 700, color: C.text, background: C.gold, borderRadius: 999, padding: "2px 8px" }}>👑 1. {lang === "fi" ? "sija" : "place"}</span>
        : <span style={{ fontSize: 10, color: C.muted }}>{rank}. {lang === "fi" ? "sija" : "place"}</span>}
    </div>
  );
  const podium = top.length === 3 && (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, alignItems: "end", marginBottom: 14 }}>
      {podiumCard(top[1], 2)}{podiumCard(top[0], 1)}{podiumCard(top[2], 3)}
    </div>
  );

  return (
    <div>
    {progress}
    {podium}
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, fontFamily: F.heading }}>🏆 {season.name}</div>
        <div style={{ fontSize: 11, color: C.muted }}>
          {lang === "fi" ? `ilta ${standings.playedNights}/${standings.totalNights}` : `night ${standings.playedNights}/${standings.totalNights}`}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: COLS, gap: 6, padding: "6px 16px", fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        <span>#</span><span>{lang === "fi" ? "Pelaaja" : "Player"}</span>
        <span style={{ textAlign: "right" }}>{lang === "fi" ? "Pist." : "Pts"}</span>
        <span style={{ textAlign: "right" }}>{lang === "fi" ? "P/ilta" : "Pts/n"}</span>
        <span style={{ textAlign: "right" }}>{lang === "fi" ? "Illat" : "Nights"}</span>
      </div>

      {standings.rows.map((r, i) => {
        const mine = isMine(r);
        const open = openKey === r.key;
        return (
          <div key={r.key} style={{ borderTop: `1px solid ${C.border}`, background: mine ? `var(--sl-accent-a10)` : undefined }}>
            <button onClick={() => setOpenKey(open ? null : r.key)} style={{
              width: "100%", background: "none", border: "none", cursor: "pointer", textAlign: "left",
              display: "grid", gridTemplateColumns: COLS, gap: 6, alignItems: "center",
              padding: "10px 16px", fontFamily: F.body,
            }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: i < 3 ? C.gold : C.muted }}>{i + 1}</span>
              <span style={{ fontSize: 14, fontWeight: mine ? 800 : 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.name}
                {!r.eligible && (
                  <span title={lang === "fi" ? `Palkintoon vähintään ${season.rules.minEvents} iltaa` : `Prizes need ${season.rules.minEvents} nights`}
                    style={{ fontSize: 10, color: C.muted, marginLeft: 6 }}>○</span>
                )}
              </span>
              <span style={{ fontSize: 15, fontWeight: 800, color: C.navy, textAlign: "right", fontFamily: F.heading }}>{r.points}</span>
              <span title={lang === "fi"
                ? `${r.points} pistettä ${r.countedNights} lasketusta illasta · ${r.wins}–${r.losses}, ${r.winPct !== null ? `${fmtAvg(r.winPct, lang)} %` : "ei otteluita"}`
                : `${r.points} points from ${r.countedNights} counted nights · ${r.wins}–${r.losses}, ${r.winPct !== null ? `${fmtAvg(r.winPct, lang)}%` : "no matches"}`}
                style={{ fontSize: 12, color: C.text, textAlign: "right" }}>
                {r.pointsPerNight !== null ? fmtAvg(r.pointsPerNight, lang) : "–"}
              </span>
              <span style={{ fontSize: 12, color: C.muted, textAlign: "right" }}>{r.played}</span>
            </button>
            {open && (
              <div style={{ padding: "0 16px 10px 50px" }}>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
                  {lang === "fi"
                    ? `${r.wins}–${r.losses} (${r.winPct !== null ? `${fmtAvg(r.winPct, lang)} %` : "–"}) · ${r.games} ${r.games === 1 ? "ottelu" : "ottelua"} · tehdyt ${r.points}, vastaan ${r.against} (${r.diff >= 0 ? "+" : ""}${r.diff})`
                    : `${r.wins}–${r.losses} (${r.winPct !== null ? `${fmtAvg(r.winPct, lang)}%` : "–"}) · ${r.games} ${r.games === 1 ? "match" : "matches"} · scored ${r.points}, against ${r.against} (${r.diff >= 0 ? "+" : ""}${r.diff})`}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {r.nights.map(n => (
                  <span key={n.eventId} title={[
                    n.counted ? "" : (lang === "fi" ? "Ei lasketa kauteen" : "Not counted in the season"),
                    n.dropped > 0 ? (lang === "fi"
                      ? `Kierto jäi kesken: ${n.dropped} ${n.dropped === 1 ? "ottelu" : "ottelua"} kierron ulkopuolella, ei laskettu`
                      : `Rotation unfinished: ${n.dropped} ${n.dropped === 1 ? "match" : "matches"} outside the rotation, not counted`) : "",
                  ].filter(Boolean).join(" · ")} style={{
                    fontSize: 11, padding: "3px 8px", borderRadius: 999,
                    background: n.counted ? C.soft : "transparent",
                    border: `1px solid ${n.counted ? C.border : "transparent"}`,
                    color: n.counted ? C.text : C.muted,
                    textDecoration: n.counted ? "none" : "line-through",
                  }}>
                    {fmtDate(n.datetime)} · {n.position !== null ? `${n.position}.` : (lang === "fi" ? "läsnä" : "present")} · {n.points} p
                    {n.games > 0 ? ` · ${n.wins}–${n.losses}` : ""}
                    {n.dropped > 0 ? " ⟳" : ""}
                  </span>
                ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div style={{ padding: "8px 16px", fontSize: 10, color: C.muted, borderTop: `1px solid ${C.border}` }}>
        {lang === "fi"
          // Alaviite on selite, ei selitys. Pitempi versio kasvatti sivua
          // puhelimessa 30–56 px eli sen verran, että taulukko ei enää
          // mahtunut ruudulle kerralla pienemmillä iPhoneilla. Säännöt
          // ovat kokonaisuudessaan heti alla avautuvassa sääntökortissa.
          ? `Pist. = tehdyt pisteet · P/ilta = per laskettu ilta · ○ = alle ${season.rules.minEvents} iltaa · napauta riviä`
          : `Pts = points scored · Pts/n = per counted night · ○ = under ${season.rules.minEvents} nights · tap a row`}
        {standings.partialNights > 0 && (
          <div style={{ marginTop: 4 }}>
            {lang === "fi"
              ? `⟳ ${standings.partialNights} ${standings.partialNights === 1 ? "illassa" : "illassa"} kierto jäi kesken — vain täydet kierrokset lasketaan`
              : `⟳ the rotation was left unfinished on ${standings.partialNights} ${standings.partialNights === 1 ? "night" : "nights"} — only full rotations count`}
          </div>
        )}
      </div>
    </div>
    </div>
  );
};
